/**
 * @file src/lib/utils/sampleResumeText.ts
 *
 * @description Plain-text resume for a sample role, built from the pre-made sample profiles used by intake. Static and
 * free: no AI call. Used by the public resume check so visitors without a resume can still try it.
 */

import { getSampleProfile } from "@/lib/sampleProfiles";
import type { UserBackground } from "@/types";

export function profileToResumeText(profile: UserBackground): string {
  const out: string[] = [];
  const { basicInfo, summary } = profile;
  out.push(basicInfo.name, [basicInfo.location, basicInfo.email].filter(Boolean).join(" | "));

  if (summary?.expertise) out.push("", "SUMMARY", summary.expertise);

  const jobs = [...(profile.workExperiences ?? []), ...(profile.additionalExperiences ?? [])];
  if (jobs.length > 0) {
    out.push("", "EXPERIENCE");
    for (const j of jobs) {
      out.push(`${j.role} | ${j.company}`, `${j.startDate} - ${j.endDate}`, String(j.description ?? ""));
    }
  }

  if ((profile.education ?? []).length > 0) {
    out.push("", "EDUCATION");
    for (const e of profile.education) {
      out.push(`${e.degree} | ${e.institution}${e.date ? ` (${e.date})` : ""}`);
    }
  }

  for (const c of profile.customSections ?? []) {
    out.push("", c.sectionName.toUpperCase(), ...(c.bullets ?? []).map((b) => `- ${b}`));
  }
  return out.join("\n").trim();
}

export function getSampleResumeText(role: string): string {
  return profileToResumeText(getSampleProfile(role));
}
